import React, { useEffect, useState } from "react";
import {
  Box,
  CssBaseline,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  IconButton,
  Autocomplete,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import AppBarComponent from "../../components/layout/AppbarComponent";
import { ToastContainer } from "react-toastify";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useConfirm } from "../../utils/useConfirm";
import units from "../../utils/units";
import api from "../../api/axiosInstance";


const STATUS_COLORS = {
  finished: "#8BC34A",
  onProgress: "#03A9F4",
  pending: "#FFC107",
};

const STATUS_LABELS = {
  finished: "ซอฟต์แวร์ที่ทำการประเมินเรียบร้อย",
  onProgress: "ซอฟต์แวร์ที่อยู่ระหว่างการประเมิน",
  pending: "ซอฟต์แวร์ที่รอรับการประเมิน",
};

const Home = () => {
  const [softwares, setSoftwares] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [filterStatus, setFilterStatus] = useState(null);

  const [editRowId, setEditRowId] = useState(null);
  const [editData, setEditData] = useState({});

  const navigate = useNavigate();
  const { confirm } = useConfirm();

  //Fetch Software + Status Chart
  const fetchSoftwares = async () => {
    try {
      const res = await api.get(`api/software`);
      const data = res.data;

      setSoftwares(data);

      const statusCount = data.reduce(
        (acc, sw) => ({ ...acc, [sw.status]: acc[sw.status] + 1 }),
        { pending: 0, onProgress: 0, finished: 0 }
      );

      const chartData = Object.entries(statusCount).map(([status, value]) => ({
        name: STATUS_LABELS[status],
        statusKey: status,
        value,
      }));

      setStatusData(chartData);
    } catch (err) {
      console.error("Failed to fetch software", err);
    }
  };

  useEffect(() => {
    fetchSoftwares();
  }, []);

  const displayedSoftwares = filterStatus
    ? softwares.filter((sw) => sw.status === filterStatus)
    : softwares;

  //Edit Handlers
  const handleEditRow = (row) => {
    setEditRowId(row.software_id);
    setEditData(row);
  };

  const handleCancelEdit = () => {
    setEditRowId(null);
    setEditData({});
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveRow = async () => {
    try {
      await api.put(`api/software/editsoftware`, editData);
      toast.success("บันทึกข้อมูลเรียบร้อยแล้ว");

      setEditRowId(null);
      fetchSoftwares();
    } catch (err) {
      toast.error("บันทึกไม่สำเร็จ");
    }
  };

  //Delete Handler
  const handleDelete = async (id) => {
    const ok = await confirm("ยืนยันการลบซอฟต์แวร์นี้?");
    if (!ok) return;

    try {
      const res = await api.delete(`api/software/delsoftware/${id}`);
      const message = res.data?.message || "ลบสำเร็จ";
      toast.success(message);

      setSoftwares((prev) => prev.filter((sw) => sw.software_id !== id));
    } catch (err) {
      const msg = err.response?.data?.error || "ลบไม่สำเร็จ";
      toast.error(msg);
    }
  };

  //DataGrid Columns
  const columns = [
    { field: "software_id", headerName: "ID", flex: 0.5 },
    {
      field: "software_name",
      headerName: "ชื่อซอฟต์แวร์",
      flex: 1,
      renderCell: (params) =>
        editRowId === params.row.software_id ? (
          <TextField
            value={editData.software_name || ""}
            onChange={(e) => {
              e.stopPropagation();
              handleEditChange("software_name", e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            size="small"
            fullWidth
          />
        ) : (
          params.value
        ),
    },
    {
      field: "owner",
      headerName: "เจ้าของ",
      flex: 1,
      renderCell: (params) =>
        editRowId === params.row.software_id ? (
          <Autocomplete
            options={units}
            value={editData.owner || ""}
            inputValue={editData.owner || ""}
            onInputChange={(event, newInputValue) => {
              event.stopPropagation();
              handleEditChange("owner", newInputValue);
            }}
            onChange={(event, newValue) => {
              event.stopPropagation();
              handleEditChange("owner", newValue || "");
            }}
            onKeyDown={(e) => e.stopPropagation()}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                fullWidth
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            )}
          />
        ) : (
          params.value
        ),
    },
    { field: "created_at", headerName: "วันที่เพิ่มเข้าระบบ", flex: 1 },

    {
      field: "status",
      headerName: "สถานะ",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            color: "#fff",
            bgcolor: STATUS_COLORS[params.value],
            textAlign: "center",
            width: "100%",
          }}
        >
          {STATUS_LABELS[params.value]}
        </Box>
      ),
    },

    {
      field: "actions",
      headerName: "การจัดการ",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const isEditing = editRowId === params.row.software_id;

        if (isEditing) {
          return (
            <>
              <IconButton color="primary" onClick={handleSaveRow}>
                <SaveIcon />
              </IconButton>

              <IconButton color="secondary" onClick={handleCancelEdit}>
                <CancelIcon />
              </IconButton>
            </>
          );
        }

        return (
          <Box
            display="flex"
            gap={1}
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
          >
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (params.row.status === "finished") {
                  navigate(`/approve/${params.row.software_id}`);
                } else {
                  navigate(`/software/edit/${params.row.software_id}`);
                }
              }}
            >
              {params.row.status === "finished" ? "ผลการประเมิน" : "ประเมิน"}
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditRow(params.row);
              }}
            >
              แก้ไข
            </Button>

            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.row.software_id);
              }}
            >
              ลบ
            </Button>
          </Box>
        );
      },
    },
  ];

  //Page Layout
  return (
    <>
      <CssBaseline />
      <AppBarComponent />
      <ToastContainer position="top-center" autoClose={2000} />

      <Box sx={{ p: 2, bgcolor: "#DFF6F0", minHeight: "100vh" }}>
        <Paper
          sx={{
            width: "95%",
            mx: "auto",
            p: 3,
            borderRadius: 2.5,
            bgcolor: "#E6F7F4",
          }}
        >
          <Typography variant="h5" mb={3} fontWeight="bold">
            สรุปข้อมูลการประเมินราคาและระยะเวลาในการพัฒนาซอฟต์แวร์
          </Typography>

          {/* Summary Boxes */}
          <Grid container spacing={2} mb={3}>
            {[
              {
                label: "งานทั้งหมด",
                value: softwares.length,
                color: "#FF6F91",
              },
              {
                label: "เสร็จสิ้น",
                value: softwares.filter((s) => s.status === "finished").length,
                color: "#8BC34A",
              },
              {
                label: "ดำเนินการ",
                value: softwares.filter((s) => s.status === "onProgress")
                  .length,
                color: "#03A9F4",
              },
              {
                label: "รอเปิดงาน",
                value: softwares.filter((s) => s.status === "pending").length,
                color: "#FFC107",
              },
            ].map((box, idx) => (
              <Grid item xs={12} sm={3} key={idx}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: box.color,
                    color: "#fff",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6">{box.label}</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {box.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Pie Chart */}
          <Box
            sx={{
              width: "100%",
              height: 300,
              bgcolor: "#fff",
              borderRadius: 2,
              p: 2,
              mb: 3,
            }}
          >
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={statusData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                  onClick={(entry) => setFilterStatus(entry.statusKey)}
                >
                  {statusData.map((entry, idx) => (
                    <Cell
                      key={idx}
                      fill={STATUS_COLORS[entry.statusKey]}
                      cursor="pointer"
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          {/* DataGrid */}
          <Box sx={{ height: 400, bgcolor: "#fff", borderRadius: 2 }}>
            <DataGrid
              rows={displayedSoftwares}
              columns={columns}
              getRowId={(row) => row.software_id}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 20]}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "#f0f0f0",
                  fontWeight: "bold",
                },
              }}
            />
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default Home;
